import { Loader } from '../../_core/loader.component';
import { ICompanyOverview, IUser } from '../../../lib/types';
import { Button } from '../../_core/button/button.component';
import { useMemo, useState } from 'react';
import { RIDialog } from '../../_core/dialog/dialog.component';
import { Tabs } from '../../_core/tabs.component';
import { activeStatus } from '../../../utils/wording.helpers';
import { TableWithActions } from '../../_core/table/table-with-actions.component';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '../../../_routes';
import { FetchResult } from '@apollo/client';
import { ISetActiveCompanyProps } from '../../domains/users/user.types';
import { UpdateUserContainer } from '../../domains/users/update/update-user-form.container';
import { showError } from '../../../lib/utils';
import { useTableFilterConfig } from '../../_core/table/utils/table-utils';
import { ProfileDetailsBox } from '../../_core/details/profile-details-box.component';
import { DetailItemProps } from '../../_core/details/details.types';
import { nameAndStatusSort, sortMethodTuple, useTableSortConfig } from '../../_core/table/utils/table-sort';
import { RegisterCompanyStepper } from '../../domains/companies/register/register-company-stepper.component';

interface IProfilePageProps {
  activeCompanyId?: string;
  companies?: ICompanyOverview[];

  isGlobalAdmin?: boolean;
  user?: IUser;

  setActiveCompany: (_: string) => Promise<FetchResult<ISetActiveCompanyProps>>;
  settingActiveCompany: boolean;
}

export const ProfilePage: React.FC<IProfilePageProps> = ({
  activeCompanyId,
  companies,
  isGlobalAdmin,
  user,
  setActiveCompany,
  settingActiveCompany
}) => {
  const navigate = useNavigate();

  const [registerCompanyOpen, setRegisterCompanyOpen] = useState(false);
  const [updateUser, setUpdateUser] = useState(false);

  const [filterConfig, filterFunc] = useTableFilterConfig({ activeStatusFilter: true });
  const filtered = useMemo(() => companies?.filter(filterFunc), [filterFunc, companies]);

  const detailItems = useMemo(() => {
    const items: DetailItemProps[] = [
      { label: 'First Name', value: user?.givenName },
      { label: 'Last Name', value: user?.familyName },
      { label: 'Email', value: user?.email }
    ];

    if (isGlobalAdmin) items.push({ label: 'Is Global Admin' });

    return items;
  }, [isGlobalAdmin, user]);

  const { sortFunc, ...restSort } = useTableSortConfig({
    defaultSortFunc: nameAndStatusSort,
    sortMethods: { name: sortMethodTuple(), deletedAt: sortMethodTuple('string', (d?: Date) => activeStatus(!d)) }
  });

  if (!user)
    return (
      <div className="mx-auto">
        <Loader />
      </div>
    );

  return (
    <>
      <ProfileDetailsBox
        actionItems={[
          <Button size="xl" onClick={() => setUpdateUser(true)}>
            Edit User
          </Button>
        ]}
        detailItems={detailItems}
        title="Personal Profile"
      />
      <div className="mt-4 flex flex-1 w-full pb-2">
        <Tabs
          tabs={[
            {
              title: 'Associated Organizations',
              content: (
                <TableWithActions
                  {...restSort}
                  bordered
                  loading={!companies}
                  headers={[
                    { content: 'Name', sortKey: 'name' },
                    { content: 'Status', sortKey: 'deletedAt' },
                    { content: '' }
                  ]}
                  values={
                    filtered?.sort(sortFunc).map(({ _id: rowId, name, deletedAt }) => ({
                      rowId,
                      values: [
                        { children: name },
                        { children: activeStatus(!deletedAt) },
                        {
                          children:
                            activeCompanyId === rowId ? (
                              <div className="h-9 flex items-center">
                                <span className="font-semibold text-primary">Selected</span>
                              </div>
                            ) : (
                              <Button
                                slim
                                loading={settingActiveCompany}
                                text="Select"
                                size="large"
                                onClick={() => {
                                  setActiveCompany(rowId)
                                    .then(() => navigate(ROUTE_PATHS.FIRM_PROFILE))
                                    .catch((err) => showError('Unable to change active organization', err));
                                }}
                              />
                            )
                        }
                      ]
                    })) ?? []
                  }
                  filterConfig={filterConfig}
                >
                  <Button onClick={() => setRegisterCompanyOpen(true)} text="Add Organization" size="xl" />
                </TableWithActions>
              )
            }
          ]}
        />
      </div>
      <RIDialog open={registerCompanyOpen} setOpen={setRegisterCompanyOpen}>
        <RegisterCompanyStepper close={() => setRegisterCompanyOpen(false)} />
      </RIDialog>
      <RIDialog open={updateUser} setOpen={setUpdateUser}>
        <UpdateUserContainer
          givenName={user.givenName}
          familyName={user.familyName}
          close={() => setUpdateUser(false)}
        />
      </RIDialog>
    </>
  );
};
